import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { PlusOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Input, message, Tooltip, Button } from 'antd';
import moment from 'moment';
import TextArea from 'antd/lib/input/TextArea';
import { FormComponentProps } from '@ant-design/compatible/lib/form';

import { useConvTag, useFunction } from '@laigu/store';
import useUpgrade from '../../hooks/use-upgrade';
import MyTag from '../my-tag';
import ConvTag, { ConvTagType } from '../conv-tag';
import ConvSummaryModal, { SummaryInfoType } from './conv-summary-modal';

import { getSummaryContentByConvId } from './api';

import style from './index.less';
import SystemTag from '../system-tag';

const timeFormat = 'YYYY-MM-DD HH:mm:ss';

type Agent = Partial<{
  avatar: string;
  name: string;
}>;

type ConvInfo = {
  content?: string;
  convTags?: Array<ConvTagType>;
  created_at?: string;
  updated_at?: string;
  agent?: Agent;
};

interface ConvSummProps extends FormComponentProps {
  // TODO: 级联组件修改为tsx后添加参数类型
  handleConvTag: (val: number[]) => Promise<void>;
  convInfo: ConvInfo;
  createConvSummary: (content: string, successCb: () => void) => void;
  updateConvSummary: (content: string, successCb: () => void) => void;
  summaryAddable: boolean;
  summaryEditable: boolean;
  convTagEditable: boolean;
  convId: string;
}

interface SummaryInfo {
  visible: boolean;
  info: SummaryInfoType | null;
}

let selectedConvTags: Array<ConvTagType> = [];

const ConversationSummary = (props: ConvSummProps): JSX.Element => {
  const {
    handleConvTag,
    form,
    convInfo,
    createConvSummary,
    updateConvSummary,
    // 将两个编辑状态提到props传值
    summaryAddable = true,
    summaryEditable = true,
    convTagEditable = true,
    convId
  } = props;

  const { convTagMap } = useConvTag();
  const { convTags: convTagsProps, content } = convInfo;
  let convTags: Array<ConvTagType> = [];
  const { bindFunctionAndPerm } = useUpgrade();
  if (convTagsProps) {
    convTags = convTagsProps;
  }
  const { getFieldDecorator } = form;
  const input = useRef<TextArea>(null);
  const [contentEditing, setContentEditing] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);

  const [summaryInfo, setSummaryInfo] = useState<SummaryInfo>({
    visible: false,
    info: null
  });

  const [loading, setLoading] = useState(false);

  const convTagFunc = useFunction('conversation_tag');

  const hasConvSummaryFun = useFunction('conv_summary');

  let notEmptyContent = false;
  // 如果是空字符串
  if (typeof content === 'string') {
    notEmptyContent = true;
  }

  useEffect(() => {
    if (convTagsProps && !tagLoading) {
      selectedConvTags = convTagsProps;
    }
  }, [convTagsProps, tagLoading]);

  const changeConvTag = async (val: number[]) => {
    selectedConvTags = val.map(id => convTagMap[id]).filter(Boolean);
    setTagLoading(true);
    await handleConvTag(val);
    setTagLoading(false);
  };

  const deleteTag = (id: number) => {
    const filter = convTags.filter(value => value.id !== id).map(val => val.id);
    changeConvTag(filter);
  };

  const changeTagsFromGpt = (vals: number[]) => {
    const oldIds = selectedConvTags.map(val => val.id);
    const innerTags = Array.from(new Set([...oldIds, ...vals]));

    changeConvTag(innerTags);
  };

  const changeConvSummary = (summary: string) => {
    const fun = notEmptyContent ? updateConvSummary : createConvSummary;
    fun(summary, () => {});
  };

  const onSave = () => {
    const value = form.getFieldsValue(['content']).content;
    const fun = notEmptyContent ? updateConvSummary : createConvSummary;
    const successCb = () => {
      setContentEditing(false);
    };
    if (!notEmptyContent) {
      if (!value || (value && !value.trim())) {
        return message.error('内容不能为空');
      }
    }
    if (notEmptyContent && value === content) {
      return successCb();
    }
    if (value && value.length > 500) {
      return message.error('内容不能大于500个字符');
    }
    return fun(value, successCb);
  };

  const getSummaryContent = (): JSX.Element => {
    const { content, created_at, updated_at, agent } = convInfo;
    const status = `content_${notEmptyContent}_edit_${contentEditing}`;
    const timeLabel = moment
      .utc(updated_at || created_at)
      .local()
      .format(timeFormat);

    switch (status) {
      case 'content_false_edit_false':
        return (
          <div style={{ justifyContent: 'flex-start' }} className={classnames(style.row, style['no-margin'])}>
            <label>{'该段对话小结'}</label>
            {summaryAddable ? (
              <span
                className={classnames(style['add-tag-other'], style['add-tag'])}
                onClick={() => setContentEditing(true)}
              >
                <PlusOutlined />
                <span>{'对话小结'}</span>
              </span>
            ) : null}
          </div>
        );
      case 'content_false_edit_true':
        return (
          <>
            <div className={classnames(style.row)}>
              <label>{'该段对话小结'}</label>
              <div>
                <span className={style.cancel} onClick={() => setContentEditing(false)}>
                  {'取消'}
                </span>
                <span
                  className={style.save}
                  onClick={bindFunctionAndPerm(onSave, 'conversation_summaries', 'change_conv_summary')}
                >
                  {'保存'}
                </span>
              </div>
            </div>
            <div>
              {getFieldDecorator('content', {
                initialValue: content
              })(<Input.TextArea ref={input} />)}
            </div>
          </>
        );
      case 'content_true_edit_false':
        return (
          <div>
            <label className={classnames(style.row, style.title)}>{'该段对话小结'}</label>
            <div className={style.row}>
              <div>
                <img src={agent?.avatar} alt={'访客头像'} className={style.avatar} />
                <span className={style.name}>{agent?.name}</span>
                <span className={style.time}>{timeLabel}</span>
              </div>
              {summaryEditable ? (
                <div onClick={() => setContentEditing(true)} className={style.edit}>
                  {'编辑'}
                </div>
              ) : null}
            </div>
            <div className={style.content}>{content}</div>
          </div>
        );
      case 'content_true_edit_true':
        return (
          <div>
            <label className={classnames(style.row, style.title)}>{'该段对话小结'}</label>
            <div className={style.row}>
              <div>
                <img src={agent?.avatar} alt={'访客头像'} className={style.avatar} />
                <span className={style.name}>{agent?.name}</span>
                <span className={style.time}>{timeLabel}</span>
              </div>
              <div>
                <span className={style.cancel} onClick={() => setContentEditing(false)}>
                  {'取消'}
                </span>
                <span
                  className={style.save}
                  onClick={bindFunctionAndPerm(onSave, 'conversation_summaries', 'change_conv_summary')}
                >
                  {'保存'}
                </span>
              </div>
            </div>
            <div>
              {getFieldDecorator('content', {
                initialValue: content
              })(<Input.TextArea ref={input} />)}
            </div>
          </div>
        );
      default:
        return <></>;
    }
  };

  const handleSummary = () => {
    setContentEditing(false);
    setLoading(true);
    getSummaryContentByConvId({ conv_id: Number(convId) })
      .then(res => {
        const { summary = '', tag_ids = [] } = res.data || {};

        const tags = tag_ids.map((id: string) => convTagMap[id]).filter(Boolean);

        setSummaryInfo({
          visible: true,
          info: {
            tags,
            summary
          }
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSummaryClose = () => {
    setSummaryInfo({
      visible: false,
      info: null
    });
  };

  useEffect(() => {
    if (contentEditing) {
      if (input.current) {
        input.current.focus();
      }
    }
  }, [contentEditing]);

  return (
    <div className={style['summary-box']}>
      {convTagFunc && (
        <div className={style['tag-box']}>
          <div className={classnames(style.row, style['tag-title'])}>
            <label>{'该段对话标签'}</label>
            {convTagEditable ? (
              <ConvTag convTags={selectedConvTags || []} onSelect={changeConvTag}>
                {selectedConvTags.length >= 10 ? (
                  <Tooltip placement='top' title={'一条对话仅可添加10个对话标签'}>
                    <span className={classnames(style['add-tag-other'], style['add-tag'])}>
                      <PlusOutlined />
                      <span>{'对话标签'}</span>
                    </span>
                  </Tooltip>
                ) : (
                  <span className={classnames(style['add-tag-other'], style['add-tag'])}>
                    <PlusOutlined />
                    <span>{'对话标签'}</span>
                  </span>
                )}
              </ConvTag>
            ) : null}
            <div className={style['tag-title-edit']}>
              {hasConvSummaryFun && (
                <Button type='link' onClick={handleSummary} loading={loading}>
                  {'系统生成总结'}
                </Button>
              )}
            </div>
          </div>
          <div className={classnames(style.row, style['tag-operate'])}>
            <div>
              {convTags.map(val => (
                <SystemTag key={val.id} data={val} closabel onClose={() => deleteTag(val.id)} />
              ))}
            </div>
          </div>
        </div>
      )}
      <div className={style['content-box']}>{getSummaryContent()}</div>
      <ConvSummaryModal
        {...summaryInfo}
        changeConvTags={changeTagsFromGpt}
        changeConvSummary={changeConvSummary}
        onClose={handleSummaryClose}
      />
    </div>
  );
};

export default Form.create<ConvSummProps>({ name: 'summary' })(ConversationSummary);
